import type { JSONContent } from '@tiptap/core';
import { parseMarkdown, serializeMarkdown } from '@nexus/markdown';
import type { Store } from '@nexus/store';
import { findByName, useCatalog } from '../state/catalog';

/** Page → Markdown with front matter, using current titles for links. */
export function pageToMarkdown(doc: JSONContent, props: Record<string, unknown>): string {
  const { byId, aliases } = useCatalog.getState();
  return serializeMarkdown(doc, {
    frontmatter: Object.keys(props).length ? props : null,
    titleOf: (id, target) =>
      aliases.get(id)?.some((a) => a.toLowerCase() === target.toLowerCase())
        ? target
        : byId.get(id)?.title,
  });
}

/** Give unresolved `[[links]]`/embeds a page id when a page with that name exists. */
export function resolveLinks(doc: JSONContent): JSONContent {
  const walk = (n: JSONContent): JSONContent => {
    if ((n.type === 'wikiLink' || n.type === 'embed') && !n.attrs?.pageId && n.attrs?.target) {
      const page = findByName(n.attrs.target as string);
      if (page) return { ...n, attrs: { ...n.attrs, pageId: page.id } };
    }
    return n.content ? { ...n, content: n.content.map(walk) } : n;
  };
  return walk(doc);
}

/**
 * Apply edited Markdown to a page: properties from the front matter (added,
 * changed and removed) and the document returned for the editor to load.
 */
export async function applyMarkdown(
  store: Store,
  pageId: string,
  markdown: string,
  currentProps: Record<string, unknown>,
): Promise<{ doc: JSONContent; invalidFrontmatter: string | null }> {
  const parsed = parseMarkdown(markdown);
  if (!parsed.invalidFrontmatter) {
    const next = parsed.frontmatter ?? {};
    for (const key of Object.keys(currentProps)) {
      if (!(key in next)) await store.setProp(pageId, key, undefined);
    }
    for (const [key, value] of Object.entries(next)) {
      if (JSON.stringify(currentProps[key]) !== JSON.stringify(value)) {
        await store.setProp(pageId, key, value);
      }
    }
  }
  return { doc: resolveLinks(parsed.doc), invalidFrontmatter: parsed.invalidFrontmatter };
}
